import React, { useState, useEffect, useContext } from 'react';
import { Redirect } from 'react-router-dom';
import { MenuItem, TextField, InputAdornment, Switch, FormGroup, FormControlLabel, FormControl } from '@material-ui/core';
import Icon from '@material-ui/core/Icon';

import { jssPreset, makeStyles } from '@material-ui/core/styles';
import { isvApi, API_URL } from '../../../services/api';
import { preTransformRequestObject, pilotPlanes, removePropFromObject } from '../../../commons/utils';
import { countryCodes, PHONE_NUMBER, PHONE_CODE, COMPANY_NAME } from '../../../commons/country';
import { ISV_PLANS_URL } from '../../../commons/constants';
import { generalMuiForm } from '../../../commons/styles';
import { FormLabel } from 'react-bootstrap';
import { ProductSelected } from '../../../hooks/contexts';


const useStyles = makeStyles((theme) => ({
    lastSelect: {
        marginBottom: theme.spacing(2.6),
        marginTop: theme.spacing(1.5),
        width: '100%',
        ...generalMuiForm,
    },
    input: {
        marginBottom: theme.spacing(1.5),
        marginTop: theme.spacing(1.5),
        width: '100%',
        ...generalMuiForm,
    },
    selectName: {
        paddingLeft: 8,
    },
    selectCode: {
        paddingRight: 0,
    },
    selectCountryName: {
        width: '100%',
        margin: 0,
        paddingLeft: 10,
    },
    selectCountryCode: {
        textAlign: 'right',
        margin: 0,
    },
    errorInputMessage: {
        color: '#f44336',
        marginLeft: 14,
        marginRight: 14,
        fontSize: '0.75rem',
        fontFamily: "Roboto",
        fontWeight: 400,
        lineHeight: 1.66,
    },
}));

const validateFormatEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return !!re.test(String(email).toLowerCase());
};

const validateFormatPhoneNumber = (number) => {
    const re = /^([0-9]){1,20}$/;
    return !!re.test(String(number).toLowerCase());
};

const addIconCode = {
    endAdornment: (
        <InputAdornment position="start">
            <Icon className="fas fa-check-circle" style={{ color: '#23c00b' }} />
        </InputAdornment>
    ),
};

const FillForm = ({ countries }) => {
    const classes = useStyles();
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [fieldsWithErrors, setFieldsWithErrors] = useState({});
    const [formFields, setFormFields] = useState({});
    const [countryCode, setCountryCode] = useState('56');
    const [inputPropsField, setInputPropsField] = useState({});
    const [loadingRequest, setLoadingRequest] = useState(false);
    const [isErrorRequest, setIsErrorRequest] = useState(false);
    const [pilotHashCode, setPilotHashCode] = useState();
    const {productsStatus, setProductsStatus} = useContext(ProductSelected);

    const formFieldNamesOnly = [
        'companyName',
        'country',
        'contact',
        'email',
    ];

    const formErrorMsg = {
        phoneNumber: 'Formato de teléfono inválido ejemplo: 957528371',
        email: 'Formato de correo inválido (ejemplo@ejemplo.com)',
        countryCode: 'Prefijo de teléfono requerido',
    };

    const validateCheckIcon = (value) => {
        if (!!(formFields[value] || '').trim()) {
            setInputPropsField({ ...inputPropsField, [value]: addIconCode });
        } else {
            setInputPropsField({ ...inputPropsField, [value]: {} });
        }
    };

    const validatePropField = (fieldName) => {
        const validateField = {
            phoneNumber: validateFormatPhoneNumber(formFields[fieldName]),
            email: validateFormatEmail(formFields[fieldName]),
        }[fieldName];
        if (validateField) {
            setInputPropsField({ ...inputPropsField, [fieldName]: addIconCode });
        } else {
            setInputPropsField({ ...inputPropsField, [fieldName]: null });
        }
    };

    const setErrorValidationField = (fieldName) => {
        let fieldsErrors;

        if (!!formFields[fieldName]) {
            const validateField = {
                phoneNumber: validateFormatPhoneNumber(formFields[fieldName]),
                email: validateFormatEmail(formFields[fieldName]),
            }[fieldName];
            if (!validateField) {
                fieldsErrors = {
                    ...fieldsWithErrors,
                    [fieldName]: formErrorMsg[fieldName] || null
                };
            } else if (fieldName === PHONE_NUMBER && !countryCode) {
                fieldsErrors = { ...fieldsWithErrors, [PHONE_CODE]: formErrorMsg[PHONE_CODE], [fieldName]: null };
            } else {
                fieldsErrors = { ...fieldsWithErrors, [fieldName]: null, ...(fieldName === PHONE_NUMBER ? { [PHONE_CODE]: null } : {}) };
            }
        } else if (!formFields[fieldName]) {
            fieldsErrors = { ...fieldsWithErrors, [fieldName]: null, ...(fieldName === PHONE_NUMBER ? { [PHONE_CODE]: null } : {}) };
        }

        setFieldsWithErrors(fieldsErrors);
    };

    useEffect(() => {
        validatePropField('email');
        setErrorValidationField('email');
    }, [formFields['email']]);
    useEffect(() => validateCheckIcon('contactLastName'), [formFields['contactLastName']]);
    useEffect(() => validateCheckIcon('contact'), [formFields['contact']]);
    useEffect(() => validateCheckIcon('companyName'), [formFields['companyName']]);

    useEffect(() => {
        validatePropField('phoneNumber');
        setErrorValidationField('phoneNumber');
    }, [formFields['phoneNumber']]);

    useEffect(() => {
        const fieldsToDelete = ['phoneNumber', 'isTc', 'isSupplai', 'isMks', 'isIsv']
        const fieldsFormValidate = JSON.parse(JSON.stringify(formFields))

        for (const [k, v] of Object.entries(fieldsFormValidate)) {
            if (fieldsToDelete.includes(k)) {
                delete fieldsFormValidate[k]
            }
        }

        if (productsStatus.isIsv) {
            formFieldNamesOnly.push('pilotPlan')
        } else if (!productsStatus.isIsv) {
            formFieldNamesOnly.pop()
        }

        if (
            !!Object.values(fieldsFormValidate).length
            && formFieldNamesOnly.every((el) => Object.keys(fieldsFormValidate).includes(el))
            && Object.values(fieldsFormValidate).every((el) => (el || '').trim())
            && Object.values(fieldsWithErrors).every((el) => !el)
            && Object.values(productsStatus).some(e => e === true)
        ) {
            setSubmitDisabled(false);
        } else {
            setSubmitDisabled(true);
        }
    }, [formFields, fieldsWithErrors]);

    const updateField = (nameField, event) => {
        setFormFields({
            ...formFields,
            [nameField]: event.target.value || event.target.checked,
        })
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (Object.values(fieldsWithErrors).every((el) => !el)) {
            setLoadingRequest(true);

            formFields.productInfo = {
                'isIsv': false,
                'isTc': false,
                'isSupplai': false,
                'isMks': false,
            };

            if (formFields.isIsv) {
                formFields.productInfo.isIsv = formFields.isIsv;
                delete formFields.isIsv;
            }

            if (formFields.isTc) {
                formFields.productInfo.isTc = formFields.isTc;
                delete formFields.isTc;
            }

            if (formFields.isSupplai) {
                formFields.productInfo.isSupplai = formFields.isSupplai;
                delete formFields.isSupplai;
            }

            if (formFields.isMks) {
                formFields.productInfo.isMks = formFields.isMks;
                delete formFields.isMks;
            }

            isvApi.post((API_URL.URLGenerator), preTransformRequestObject({
                ...formFields,
                company_real_name: formFields.companyName,
                prefix: formFields.companyName.toLowerCase().replaceAll(' ','_'),
                ...(countryCode && formFields.phoneNumber ? { phoneNumber: `+${countryCode} ${formFields.phoneNumber}` } : {} ),
            }))
                .then((response) => {
                    setPilotHashCode(response.data.detail);
                })
                .catch((info) => {
                    setIsErrorRequest(true);
                    const error = info.response.data;

                    if (error.errors) {
                        let formatError = {}
                        if (error.errors.name) formatError[COMPANY_NAME] = error.errors.name;

                        setFieldsWithErrors({...fieldsWithErrors, ...formatError });
                        setInputPropsField({ ...inputPropsField, [COMPANY_NAME]: {} });
                    }
                })
                .finally(() => setLoadingRequest(false));
        }
    };

    useEffect(() => {
        if (isErrorRequest) {
            setTimeout(() => setIsErrorRequest(false), 10000);
        }
    }, [isErrorRequest]);

    const handleProductChange = (e) => {
        setProductsStatus({
            ...productsStatus,
            [e.target.name]: e.target.checked
        });
    }

    if (pilotHashCode) {
        return <Redirect
            to={`/clientes/${pilotHashCode}`}
        />
    } else {
        return (
            <form className={classes.root} noValidate autoComplete="off" id="first-form">
                <div className="row">
                    <div className="col-12">
                        <h6 className="title-6 form-subtitle">Ingresa tus datos personales</h6>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-6 col-xs-12">
                        <TextField
                            id="outlined-basic"
                            label="Empresa"
                            variant="outlined"
                            className={classes.input}
                            margin="dense"
                            name="companyName"
                            onChange={(e) => {
                                setFieldsWithErrors({...fieldsWithErrors, [COMPANY_NAME]: null });
                                updateField('companyName', e);
                            }}
                            error={!!fieldsWithErrors['companyName']}
                            helperText={fieldsWithErrors['companyName'] || ''}
                            InputProps={inputPropsField['companyName']}
                        />
                    </div>
                    <div className="col-sm-6 col-xs-12">
                        <TextField
                            id="outlined-basic"
                            label="Correo electrónico"
                            variant="outlined"
                            className={classes.input}
                            margin="dense"
                            name="email"
                            onChange={(e) => updateField('email', e)}
                            error={!!fieldsWithErrors['email']}
                            helperText={fieldsWithErrors['email'] || ''}
                            InputProps={inputPropsField['email']}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-6 col-xs-12">
                        <TextField
                            id="outlined-select-currency"
                            select
                            label="País"
                            variant="outlined"
                            className={classes.input}
                            margin="dense"
                            name="country"
                            onChange={(e) => updateField('country', e)}
                            >
                                {(countries || []).map((element) => (
                                    <MenuItem
                                        key={element.iso2_code}
                                        value={element.iso2_code}
                                    >
                                        {element.country}
                                    </MenuItem>
                                ))}
                        </TextField>
                    </div>
                    <div className="col-sm-6">
                        <div className="row">
                            <div className={`col-3 ${classes.selectCode}`}>
                                <TextField
                                    id="outlined-select-currency"
                                    select
                                    label={`+${countryCode || '56'}`}
                                    variant="outlined"
                                    className={classes.input}
                                    margin="dense"
                                    name="countryCode"
                                    value={countryCode}
                                    error={!!fieldsWithErrors['countryCode']}
                                    InputProps={inputPropsField['countryCode']}
                                    onChange={(e) => {
                                        setFieldsWithErrors({...fieldsWithErrors, [PHONE_CODE]: null });
                                        setCountryCode(e.target.value);
                                    }}
                                    >
                                        {countryCodes.filter((countryCode) => countries.map((country) => (country.iso2_code)).includes(countryCode.iso2))
                                        .map((element) => (
                                            <MenuItem
                                                key={element.iso2}
                                                value={element.phone_code}
                                            >
                                                <img
                                                    alt={`${element.iso2}`}
                                                    width="16px"
                                                    src={`http://purecatamphetamine.github.io/country-flag-icons/3x2/${element.iso2}.svg`}
                                                />
                                                <label className={classes.selectCountryName}>{element.label}</label>
                                                <label className={classes.selectCountryCode}>+{element.phone_code}</label>
                                            </MenuItem>
                                        ))}
                                </TextField>
                            </div>
                            <div className={`col-9 ${classes.selectName}`}>
                                <TextField
                                    id="outlined-basic"
                                    label="Teléfono (Opcional)"
                                    variant="outlined"
                                    className={classes.input}
                                    margin="dense"
                                    name="phoneNumber"
                                    onChange={(e) => updateField('phoneNumber', e)}
                                    error={!!fieldsWithErrors['phoneNumber']}
                                    InputProps={inputPropsField['phoneNumber']}
                                />
                            </div>
                        </div>
                    </div>
                    {!!fieldsWithErrors['phoneNumber'] || !!fieldsWithErrors['countryCode']
                        ?
                            <React.Fragment>
                                <div className="col-sm-6" />
                                <div className="col-sm-6 col-xs-12">
                                    <p className={classes.errorInputMessage}>
                                        {fieldsWithErrors['phoneNumber'] || fieldsWithErrors['countryCode']}
                                    </p>
                                </div>
                            </React.Fragment>
                        : ''}
                </div>
                <div className="row">
                    <div className="col-sm-12 col-xs-12">
                        <TextField
                            id="outlined-basic"
                            label="Contacto clave (Nombre completo)"
                            variant="outlined"
                            className={classes.input}
                            margin="dense"
                            name="contact"
                            onChange={(e) => updateField('contact', e)}
                            InputProps={inputPropsField['contact']}
                        />
                    </div>
                </div>
                {productsStatus.isIsv && <div className="row">
                    <div className="col-sm-12">
                        <TextField
                            id="outlined-select-currency"
                            select
                            label="Plan ISV a pilotear"
                            variant="outlined"
                            className={classes.lastSelect}
                            margin="dense"
                            name="pilotPlan"
                            onChange={(e) => updateField('pilotPlan', e)}
                            FormHelperTextProps={{
                                style: { marginLeft: 0 },
                            }}
                            helperText={(
                                <a
                                    className="text-link"
                                    href={ISV_PLANS_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    ¿No sabes qué versión?
                                </a>
                            )}
                        >
                            {pilotPlanes.map((element) => (
                                <MenuItem
                                    key={element.key}
                                    value={element.value}
                                >
                                    {element.key}
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>
                </div>}
                <div className="row">
                    <FormControl component="fieldset">
                        <FormLabel component="legend">
                            Selecciona el(los) producto(s) a pilotear
                        </FormLabel>
                        <FormGroup aria-label="position" row>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="isTc"
                                        onChange={e => {
                                            handleProductChange(e);
                                            updateField('isTc', e);
                                        }}
                                        checked={productsStatus.isTc}
                                    />
                                }
                                label="Teamcore"
                                labelPlacement="start"
                                style={{"marginRight": "10px"}}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="isIsv"
                                        onChange={e => {
                                            handleProductChange(e);
                                            updateField('isIsv', e);
                                        }}
                                        checked={productsStatus.isIsv}
                                    />
                                }
                                label="Instoreview"
                                labelPlacement="start"
                                style={{"marginRight": "10px"}}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="isSupplai"
                                        onChange={e => {
                                            handleProductChange(e);
                                            updateField('isSupplai', e);
                                        }}
                                        checked={productsStatus.isSupplai}
                                    />
                                }
                                label="Supplai"
                                labelPlacement="start"
                                style={{"marginRight": "10px"}}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="isMks"
                                        onChange={e => {
                                            handleProductChange(e);
                                            updateField('isMks', e);
                                        }}
                                        checked={productsStatus.isMks}
                                    />
                                }
                                label="MarketShare"
                                labelPlacement="start"
                                style={{"marginRight": "10px"}}
                            />
                        </FormGroup>
                    </FormControl>
                </div>
                <div className="row justify-content-center m-3">
                    <div className="col-sm-12">
                        <button
                            className="isv-btn isv-btn-circle btn-red btn-block m-auto"
                            disabled={submitDisabled || loadingRequest}
                            onClick={(e) => handleSubmit(e)}
                        >
                            {loadingRequest &&
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            } {' '}
                            Siguiente
                        </button>
                        {isErrorRequest &&
                            <span className="text text-bad-center">No ha sido posible procesar la petición, por favor intente más tarde</span>
                        }
                    </div>
                </div>
            </form>
        );
    }
};

export default FillForm;
